import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Category } from '../test-manager.component';
import { HttpService } from 'src/app/service/http/http.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatSelectionList, MatListOption } from '@angular/material/list';

interface DialogData {
  categories: Category[];
  title: string;
  justCategory?: boolean;
  origin?: string;
}

@Component({
  selector: 'incluir-json-modal',
  templateUrl: './incluir-json-modal.component.html',
  styleUrls: ['./incluir-json-modal.component.scss']
})
export class IncluirJsonModalComponent implements OnDestroy, OnInit {
  selectedFiles: File[] = [];
  fileLimitExceeded = false;
  fileNames = []
  categories: string[] = ['Performance', 'Funcional', 'Segurança'];
  selectedCategory: string | null = null;
  showCreate = false;
  showEdit = false;
  showDelete = false;

  selectedItems: Category[] = [];
  isAllSelected = false;
  private isTogglingAll = false;

  @ViewChild('list') selectionList!: MatSelectionList;

  novaCategoria = '';
  searchEditTerm = '';
  searchDeleteTerm = '';
  hasChanges = false;
  origin = 'QG';

  private $unsubscribelAll = new Subject<void>();

  get isCreateDisabled(): boolean {
    return this.showEdit || this.showDelete;
  }

  get isEditDisabled(): boolean {
    return this.showCreate || this.showDelete;
  }

  get isDeleteDisabled(): boolean {
    return this.showCreate || this.showEdit;
  }
  
  constructor(
    public dialogRef: MatDialogRef<IncluirJsonModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private readonly http: HttpService
  ) {}

  ngOnInit(): void {
    if(this.data.origin){
      this.origin = this.data.origin;
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files = Array.from(input.files);
      this.fileNames = Array.from(input.files).map(item => item.name)
      if (files.length > 500) {
        this.fileLimitExceeded = true;
        this.selectedFiles = [];
      } else {
        this.fileLimitExceeded = false;
        this.selectedFiles = files;
      }
    }

  }

  get filteredCategoriesForEdit(): Category[] {
    if (!this.searchEditTerm.trim()) {
      return this.data.categories;
    }
    const term = this.searchEditTerm.toLowerCase().trim();
    return this.data.categories.filter(category => 
      category.name.toLowerCase().includes(term)
    );
  }

  get filteredCategoriesForDelete(): Category[] {
    if (!this.searchDeleteTerm.trim()) {
      return this.data.categories;
    }
    const term = this.searchDeleteTerm.toLowerCase().trim();
    return this.data.categories.filter(category => 
      category.name.toLowerCase().includes(term)
    );
  }

  toggleSelectAll(list: MatSelectionList, event: Event) {
    event.preventDefault();
    event.stopPropagation();
    
    this.isTogglingAll = true;
    
    const optionsArray = list.options.toArray();
    const filteredCategories = this.filteredCategoriesForDelete;
    
    // Verifica se todas as categorias filtradas já estão selecionadas
    const allFilteredSelected = filteredCategories.length > 0 && 
      filteredCategories.every(cat => this.selectedItems.includes(cat));
    
    if (allFilteredSelected) {
      // Deseleciona todas as categorias filtradas (pula a primeira opção que é "Selecionar todos")
      optionsArray.forEach((option: MatListOption, index: number) => {
        if (index > 0 && filteredCategories.includes(option.value)) {
          option.selected = false;
        }
      });
      // Remove apenas as categorias filtradas dos itens selecionados
      this.selectedItems = this.selectedItems.filter(item => 
        !filteredCategories.includes(item)
      );
      this.isAllSelected = false;
    } else {
      // Seleciona todas as categorias filtradas (pula a primeira opção que é "Selecionar todos")
      optionsArray.forEach((option: MatListOption, index: number) => {
        if (index > 0 && filteredCategories.includes(option.value)) {
          option.selected = true;
        }
      });
      // Adiciona as categorias filtradas aos itens selecionados (sem duplicatas)
      filteredCategories.forEach(category => {
        if (!this.selectedItems.includes(category)) {
          this.selectedItems.push(category);
        }
      });
      this.isAllSelected = true;
    }
    
    // Atualiza o estado da opção "Selecionar todos"
    const selectAllOption = optionsArray[0];
    if (selectAllOption) {
      selectAllOption.selected = this.isAllSelected;
    }
    
    this.isTogglingAll = false;
  }

  onSelectionChange(change: any) {
    if (this.isTogglingAll) {
      return; // Ignora mudanças durante o toggle manual
    }
    
    const selectedOptions = change.source.selectedOptions.selected;
    // Filtra apenas as categorias (exclui a opção "Selecionar todos" que não tem value)
    this.selectedItems = selectedOptions
      .map((option: MatListOption) => option.value)
      .filter((value: any) => value !== undefined && value !== null);
    
    // Verifica se todas as categorias filtradas estão selecionadas
    const filteredCategories = this.filteredCategoriesForDelete;
    const allFilteredSelected = 
      filteredCategories.length > 0 && 
      filteredCategories.every(cat => this.selectedItems.includes(cat));
    
    this.isAllSelected = allFilteredSelected;
    
    // Atualiza o estado da opção "Selecionar todos"
    const optionsArray = change.source.options.toArray();
    if (optionsArray && optionsArray.length > 0) {
      const selectAllOption = optionsArray[0];
      selectAllOption.selected = allFilteredSelected;
    }
  }

  excluirCategorias() {
    if (this.selectedItems.length === 0) {
      return;
    }
    
    this.http.genericDeleteBody('categories', {ids: this.selectedItems.map(item => item._id)}).pipe(takeUntil(this.$unsubscribelAll)).subscribe(
      next => {
        this.hasChanges = true
        this.data.categories = this.data.categories.filter(
          category => !this.selectedItems.includes(category)
        );
        this.selectedItems = [];
        this.isAllSelected = false;
      }
    )

  }


  toggleCreate() {
    if (this.showCreate) {
      this.showCreate = false;
      this.novaCategoria = '';
    } else {
      this.showCreate = true;
      this.showEdit = false;
      this.showDelete = false;
      this.searchEditTerm = '';
      this.searchDeleteTerm = '';
    }
  }

  toggleEdit() {
    if (this.showEdit) {
      this.showEdit = false;
      this.searchEditTerm = '';
    } else {
      this.showEdit = true;
      this.showCreate = false;
      this.showDelete = false;
      this.novaCategoria = '';
      this.searchDeleteTerm = '';
    }
  }

  toggleDelete() {
    if (this.showDelete) {
      this.showDelete = false;
      this.searchDeleteTerm = '';
      this.selectedItems = [];
      this.isAllSelected = false;
    } else {
      this.showDelete = true;
      this.showCreate = false;
      this.showEdit = false;
      this.novaCategoria = '';
      this.searchEditTerm = '';
    }
  }

  criarCategoria() {
    if (!this.novaCategoria.trim()) return;
    this.http.genericPost('categories', { name: this.novaCategoria.trim() }, {}).pipe(takeUntil(this.$unsubscribelAll)).subscribe({
      next: (response: Category) => {
        this.hasChanges = true
        this.data.categories.push(response);
      }
    })
    this.novaCategoria = '';
    this.showCreate = false;
  }

  onSubmit(): void {
    if (this.selectedFiles.length === 0 && !this.data.justCategory) return;
    this.dialogRef.close(!this.data.justCategory ? {
      files: this.selectedFiles,
      category: this.selectedCategory,
      origin: this.origin?.trim() || 'QG'
    } : {
      category: this.selectedCategory,
      origin: this.origin?.trim() || 'QG'
    });
  }

  edit(item: any) {
    item.editing = true;
  }

  save(item: any) {
    this.http.genericPut(`categories`, { name: item.name }, item._id).pipe(takeUntil(this.$unsubscribelAll)).subscribe({
      next: (response: Category) => {
        this.hasChanges = true;
        item.editing = false;
      },
      error: (error) => {
        console.error('Erro ao atualizar a categoria:', error);
      }
    })
  }

  ngOnDestroy(): void {
    this.$unsubscribelAll.next();
    this.$unsubscribelAll.complete();
  }

}